/* eslint no-plusplus: ["error", { "allowForLoopAfterthoughts": true }] */

import styled from 'styled-components';
import uniqueId from 'lodash/uniqueId';
import { mq } from '~/styles/grid';

import { unicodes } from '~/components/organisms/Quote/Quote.helpers';

const PaginationContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  margin-top: 30px;

  @media (${mq.smDown}) {
    justify-content: space-evenly;
  }
`;

const PaginationButton = styled.button`
  width: 34px;
  height: 30px;
  margin: 0 3px;
  color: ${(props) =>
    props.disabled ? props.theme.color.gray500 : props.theme.color.primary800};
  background-color: ${(props) =>
    props.isActive ? props.theme.color.gray50 : props.theme.color.white500};
  border: none;
  &:hover {
    color: ${(props) =>
      props.isActive ? props.theme.color.primary800 : props.theme.color.cta};
  }
`;

/**
 * @param {number} amountPages
 * @param {number} activePage
 * @param {number} amountPageNumbers
 * @param {Function} handleJumpToPage
 * @returns {React.ReactElement}
 */
const renderPageNumbers = (
  amountPages,
  activePage,
  amountPageNumbers,
  handleJumpToPage,
) => {
  const currentPageNumberGroup = Math.floor(activePage / amountPageNumbers);

  /**
   * @type {Array<React.ReactElement>}
   */
  const buttons = [];

  for (let i = 0; i < amountPageNumbers; i++) {
    const pageNumber = currentPageNumberGroup * amountPageNumbers + i;
    if (pageNumber + 1 <= amountPages) {
      buttons.push(
        <PaginationButton
          key={uniqueId(pageNumber.toString())}
          isActive={activePage === pageNumber}
          onClick={() => handleJumpToPage(pageNumber)}
        >
          {pageNumber + 1}
        </PaginationButton>,
      );
    }
  }
  return (
    <>
      {currentPageNumberGroup > 0 && ( // Jump to last number of previous group
        <PaginationButton
          key="goToPrevBlock"
          onClick={() =>
            handleJumpToPage(currentPageNumberGroup * amountPageNumbers - 1)
          }
        >
          {unicodes.HorizontalEllipsis}
        </PaginationButton>
      )}
      {buttons}
      {activePage + amountPageNumbers < amountPages && ( // Jump to first number of next group
        <PaginationButton
          key="goToNextBlock"
          onClick={() =>
            handleJumpToPage(
              currentPageNumberGroup * amountPageNumbers + amountPageNumbers,
            )
          }
        >
          {unicodes.HorizontalEllipsis}
        </PaginationButton>
      )}
    </>
  );
};

/**
 * @param {PaginationProps} props
 * @returns {React.ReactElement} The rendered component
 */
const Pagination = ({
  amountPages,
  amountPageNumbers,
  activePage,
  handleJumpToPage,
}) => {
  return (
    <PaginationContainer>
      <PaginationButton
        disabled={activePage === 0}
        onClick={() => handleJumpToPage(0)}
      >
        {'<<'}
      </PaginationButton>
      <PaginationButton
        disabled={activePage === 0}
        onClick={() => handleJumpToPage(activePage - 1)}
      >
        {'<'}
      </PaginationButton>
      {activePage >= amountPageNumbers && (
        <PaginationButton onClick={() => handleJumpToPage(0)}>
          1
        </PaginationButton>
      )}
      {renderPageNumbers(
        amountPages,
        activePage,
        amountPageNumbers,
        handleJumpToPage,
      )}
      {activePage <= amountPages - amountPageNumbers - 1 && (
        <PaginationButton onClick={() => handleJumpToPage(amountPages - 1)}>
          {amountPages}
        </PaginationButton>
      )}
      <PaginationButton
        disabled={activePage >= amountPages - 1}
        onClick={() => handleJumpToPage(activePage + 1)}
      >
        {'>'}
      </PaginationButton>
      <PaginationButton
        disabled={activePage >= amountPages - 1}
        onClick={() => handleJumpToPage(amountPages - 1)}
      >
        {'>>'}
      </PaginationButton>
    </PaginationContainer>
  );
};

export default Pagination;
